import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Tooltip } from '@mui/material'
import SimpleBar from 'simplebar-react'
import {
  // Edit01, // re-enable together with the Employment "Edit" action below
  XClose,
} from '@untitled-ui/icons-react'
import { format } from 'date-fns'
import {
  MyHorizontalTabV2,
} from '@interstellar-component'
import TruncatedText from '@src/components/TruncatedText'
import ActivityTimeline from '@src/components/ActivityTimeline'
import Service from '../service'

const TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      backgroundColor: '#0C111D',
      padding: '12px',
      borderRadius: '8px',
      boxShadow: '0px 4px 6px -2px #10182808',
    },
  },
  arrow: { sx: { color: '#0C111D !important' } },
}

const TAB_TYPE = {
  Overview: 'overview',
  Employment: 'employment',
  History: 'history',
}

function formatDate(d, pattern = 'd MMM yyyy') {
  if (!d) return '-'
  try {
    return format(new Date(d), pattern)
  } catch {
    return '-'
  }
}

// ── helpers ────────────────────────────────────────────────────────────────────
function CirclesBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-50">
      {/* Concentric rings emanating from the top-left corner — origin sits
          further off-canvas (-140/-140) and the inner ring shrinks to 64px
          so the avatar nests inside the smallest visible circle, matching
          the figma. */}
      <div className="absolute top-[-140px] left-[-140px] w-[320px] h-[320px] pointer-events-none">
        {[64, 120, 176, 232, 288, 320].map((size, index) => (
          <div
            key={index}
            className="absolute top-1/2 left-1/2 rounded-full border border-gray-100"
            style={{
              width: `${size}px`,
              height: `${size}px`,
              transform: 'translate(-50%, -50%)',
            }}
          />
        ))}
      </div>
    </div>
  )
}

function DetailRow({ label, value }) {
  const ref = useRef(null)
  const [truncated, setTruncated] = useState(false)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return undefined
    const check = () => setTruncated(el.scrollWidth > el.clientWidth + 1)
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => ro.disconnect()
  }, [value])

  const display = value ?? '-'

  return (
    <div className="flex items-center justify-between gap-3 py-3 border-b border-gray-100 last:border-0">
      <span className="shrink-0 text-sm text-gray-500 font-medium">{label}</span>
      <div className="flex max-w-[70%] min-w-0 items-center justify-end">
        <Tooltip
          arrow
          placement="top"
          title={truncated ? display : ''}
          componentsProps={TOOLTIP_PROPS}
        >
          <span
            ref={ref}
            className="block w-full truncate text-right text-sm text-gray-900 font-semibold"
          >
            {display}
          </span>
        </Tooltip>
      </div>
    </div>
  )
}

function SectionCard({ heading, action, children }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/30 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-white">
        <h4 className="text-sm font-bold text-gray-900 tracking-tight">
          {heading}
        </h4>
        {action}
      </div>
      <div className="bg-white px-5 py-2">{children}</div>
    </div>
  )
}

// ── tab panels ──────────────────────────────────────────────────────────────
function OverviewPanel({ employee, data, loading }) {
  const o = data?.overview || {}
  // Address line: best-effort join of the structured city/region columns.
  const addressLine = [o.current_address, o.current_city, o.current_region, o.current_postal_code]
    .filter(Boolean)
    .join(', ')
  return (
    <SectionCard heading="Overview">
      {loading && !data ? (
        <p className="py-3 text-sm text-gray-400">Loading…</p>
      ) : (
        <>
          <DetailRow label="Full name" value={employee.label} />
          <DetailRow label="Date of birth" value={o.date_of_birth ? formatDate(o.date_of_birth) : '-'} />
          <DetailRow label="Gender" value={o.gender ? String(o.gender).replace(/^./, (c) => c.toUpperCase()) : '-'} />
          <DetailRow label="Marital status" value={o.marital_status ? String(o.marital_status).replace(/^./, (c) => c.toUpperCase()) : '-'} />
          <DetailRow label="ID number" value={o.id_number} />
          <DetailRow label="Address" value={addressLine || '-'} />
          <DetailRow label="Email" value={employee.email} />
          <DetailRow label="WhatsApp" value={employee.whatsapp} />
        </>
      )}
    </SectionCard>
  )
}

function EmploymentPanel({ employee, data, loading }) {
  // Prefer fetched detail fields when available; fall back to props (passed
  // from the list slider) so the panel paints immediately on hover and only
  // enriches once the BE responds.
  const e = data?.employment || {}
  const level = e.employment_level?.name || employee.level?.label || '-'
  const position = e.position?.name || employee.position || '-'
  const entity = e.company?.name || employee.entityObj?.label || '-'
  return (
    <SectionCard
      heading="Employment"
      /* Edit hidden until editing employee data during the report-enquiry
         create-employee flow is supported (was a disabled placeholder).
         Re-enable with the Edit01 import above.
      action={
        <button
          type="button"
          disabled
          className="flex items-center gap-1.5 text-brand/700 hover:text-brand/800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Edit01 className="size-4" />
          <span className="text-sm font-semibold">Edit</span>
        </button>
      } */
    >
      {loading && !data ? (
        <p className="py-3 text-sm text-gray-400">Loading…</p>
      ) : (
        <>
          <DetailRow label="Level" value={level} />
          <DetailRow label="Position" value={position} />
          <DetailRow label="Entity" value={entity} />
          <DetailRow label="WhatsApp" value={employee.whatsapp} />
          <DetailRow label="Email" value={employee.email} />
        </>
      )}
    </SectionCard>
  )
}

function HistoryPanel({ data, loading }) {
  const activity = data?.history?.activity || []
  return (
    <SectionCard heading="Activity">
      {loading && !data ? (
        <p className="py-3 text-sm text-gray-400">Loading…</p>
      ) : (
        <ActivityTimeline items={activity} emptyText="No activity yet." />
      )}
    </SectionCard>
  )
}

// ── main component ─────────────────────────────────────────────────────────────
function EmployeeDetailHoverCard({ employee, isVisible, style, onMouseEnter, onMouseLeave, onClose }) {
  const [activeTab, setActiveTab] = useState('Employment')
  // Per-tab cache so re-hovering a tab doesn't re-fetch.
  const [tabData, setTabData] = useState({})
  const [tabLoading, setTabLoading] = useState(false)

  // Reset on employee change.
  useEffect(() => {
    setActiveTab('Employment')
    setTabData({})
    setTabLoading(false)
  }, [employee?.id])

  // Lazy-fetch the current tab's payload.
  useEffect(() => {
    if (!employee?.id || !isVisible) return
    if (tabData[activeTab]) return
    const type = TAB_TYPE[activeTab]
    if (!type) return
    let canceled = false
    setTabLoading(true)
    Service.getEmployeeDetail(employee.id, { type })
      .then((res) => {
        if (canceled) return
        setTabData((prev) => ({ ...prev, [activeTab]: res?.data || {} }))
      })
      .catch(() => {})
      .finally(() => {
        if (!canceled) setTabLoading(false)
      })
    return () => { canceled = true }
  }, [employee?.id, activeTab, isVisible, tabData])

  if (!employee || !isVisible) return null

  const data = tabData[activeTab]

  return (
    <div
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`fixed z-[1100] w-[375px] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden transition-all duration-300 transform ${
        isVisible
          ? 'opacity-100 translate-x-0'
          : 'opacity-0 -translate-x-4 pointer-events-none'
      } flex flex-col max-h-[calc(100vh-48px)]`}
    >
      <SimpleBar forceVisible="y" style={{ maxHeight: 'calc(100vh - 48px)' }}>
        <div className="flex flex-col">
          {/* ── Header Decor ────────────────────────────────────────────────────── */}
          <div className="relative h-[64px] bg-white shrink-0">
            <CirclesBackground />

            {/* Close button (top right) */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-3 right-3 p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XClose size={20} />
            </button>
          </div>

          {/* ── Employee Info ───────────────────────────────────────────────────── */}
          <div className="px-5 pt-3 pb-3">
            <div className="flex items-center gap-2 mb-0.5">
              <TruncatedText
                as="p"
                text={employee.label}
                className="min-w-0 flex-1 text-base font-bold text-gray-900"
              />
              {employee.status === 'Expired' && (
                <span className="shrink-0 rounded-full bg-error/50 border border-error/200 px-2 py-0.5 text-xs font-semibold text-error/700">
                  Expired
                </span>
              )}
            </div>
            <TruncatedText
              as="p"
              text={employee.employeeId || '-'}
              className="text-sm font-semibold text-gray-900 mb-0.5"
            />
            <TruncatedText
              as="p"
              text={employee.position || ''}
              className="text-sm text-gray-500 font-medium pb-3"
            />

            {/* Navigation Tabs */}
            <div className="mb-3">
              <MyHorizontalTabV2
                fitContent
                value={activeTab}
                onChange={setActiveTab}
                tabs={[
                  { label: 'Overview', value: 'Overview' },
                  { label: 'Employment', value: 'Employment' },
                  { label: 'History', value: 'History' },
                ]}
              />
            </div>

            {/* ── Tab Panels ─────────────────────────────────────────────────────── */}
            {activeTab === 'Overview' && (
              <OverviewPanel employee={employee} data={data} loading={tabLoading} />
            )}
            {activeTab === 'Employment' && (
              <EmploymentPanel employee={employee} data={data} loading={tabLoading} />
            )}
            {activeTab === 'History' && (
              <HistoryPanel data={data} loading={tabLoading} />
            )}
          </div>

          {/* ── Padding for bottom ── */}
          <div className="h-4 shrink-0" />
        </div>
      </SimpleBar>
    </div>
  )
}

export default EmployeeDetailHoverCard
